/**
 * @file: 拖拽辅助器
 * @author: xiaolong.huang@dmall.com
 */

import * as Toolkit from '../../toolkit/index';
import * as CubeTree from '../../cubeTree/index';
import CodiSystem from '../../codiSystem/index';
import Select from '../../interaction/select/index';
import Config from '../../config/index';
import Utils from '../../utils/util';
import { findDragTarget } from './findDragTarget';

interface ChildRelativeRectMap {
    [key: string]: CubePaintCoordinate
}

class DragTransfer {
    public $dragTransfer: JQuery<HTMLElement>;
    private $mask: JQuery<HTMLElement>;
    private $sketchpad: JQuery<HTMLElement>;
    private catchCube: CubeId;
    private disX: LogicsicsMeasurement = 0;
    private disY: LogicsicsMeasurement = 0;
    private dragTargets: CubeId[];
    private transferRect: Rect;
    private childRelativeRect: CubePaintCoordinate[];
    private childRelativeRectMap: ChildRelativeRectMap;
    private pressCube: CubeId;
    public $target: JQuery<EventTarget>;
    dragCubePaintCoordinate: CubePaintCoordinate[] = [];
    fixPosition: (...args) => void;

    init() {
        this.$dragTransfer = $('#shelvesbuild__drag-transfer');
        this.$mask = $('#shelvesbuild-e-sketchpad__mask');
        this.$sketchpad = $('.shelvesbuild-e-sketchpad');
    }

    // 每次拖拽动作只会触发一次 create
    create(mousePoint: MousePoint, dragCube: CubeId): void {
        let clientX: LogicsicsMeasurement = mousePoint.left;
        let clientY: LogicsicsMeasurement = mousePoint.top;
        let allSelect: CubeId[] = Select.getAll();
        let allDragPaintCoordinate: PaintCoordinate[] = [];
        let temp: CubePaintCoordinate[] = [];
        let dragCubeInfo: DragTargetInfo = findDragTarget(allSelect, dragCube);

        Select.removeGroup(dragCubeInfo.clearCube);
        this.childRelativeRectMap = {};
        this.pressCube = dragCube;
        this.dragTargets = dragCubeInfo.dragCube;
        this.stopAnimation(this.dragTargets);
        this.dragTargets.forEach((dragTarget: CubeId): void => {
            let coordinate: PaintCoordinate = CodiSystem.getPaintCoordinate(dragTarget);

            allDragPaintCoordinate.push(coordinate);
            temp.push({
                id: dragTarget,
                ...coordinate
            });
        });
        this.dragCubePaintCoordinate = temp;
        this.transferRect = Toolkit.createRectsBorder(allDragPaintCoordinate);
        this.childRelativeRect = this.createChildRect(temp, this.transferRect);

        let $childItem: JQuery<HTMLElement> = this.createChildItem(this.childRelativeRect);
        let $dragMask: JQuery<HTMLElement> = this.createMask();

        this.disX = Utils.math(clientX, this.transferRect.left, '-');
        this.disY = Utils.math(clientY, this.transferRect.top, '-');
        Toolkit.html(this.$dragTransfer, '');
        this.$dragTransfer.css({
            width: this.transferRect.width,
            height: this.transferRect.height,
            transform: `translate(${this.transferRect.left}px, ${this.transferRect.top}px)`
        }).append($childItem).append($dragMask);
        this.$mask.addClass('show');
    }

    setMouseDownTarget(ev: JQuery.MouseDownEvent): void {
        this.$target = $(ev.target);
    }

    // 获取拖拽对象最新渲染坐标
    getDragCubePaintCoordinate(): CubePaintCoordinate[] {
        let result: CubePaintCoordinate[] = [];

        this.childRelativeRect.forEach((coordinate: CubePaintCoordinate): void => {
            result.push({
                id: coordinate.id,
                width: coordinate.width,
                height: coordinate.height,
                left: Utils.math(this.transferRect.left, coordinate.left, '+'),
                top: Utils.math(this.transferRect.top, coordinate.top, '+')
            });
        });

        return result;
    }

    getTargetCubePaintCoordinate(target: CubeId): CubePaintCoordinate {
        let targetCoordinate: CubePaintCoordinate = this.childRelativeRectMap[target];

        return {
            id: targetCoordinate.id,
            width: targetCoordinate.width,
            height: targetCoordinate.height,
            left: Utils.math(this.transferRect.left, targetCoordinate.left, '+'),
            top: Utils.math(this.transferRect.top, targetCoordinate.top, '+')
        };
    }

    getTargetCubePaintCoordinates(targets: CubeId[]): CubePaintCoordinate[] {
        return targets.map((target: CubeId): CubePaintCoordinate => {
            return this.getTargetCubePaintCoordinate(target);
        });
    }

    move(ev: JQuery.MouseMoveEvent): void {
        let left: LogicsicsMeasurement = Utils.math(ev.clientX, this.disX, '-');
        let top: LogicsicsMeasurement = Utils.math(ev.clientY, this.disY, '-');

        this.transferRect.left = left;
        this.transferRect.top = top;
        this.fixPosition && this.fixPosition(this);
        this.$dragTransfer.css({
            transform: `translate(${this.transferRect.left}px, ${this.transferRect.top}px)`
        });
    }

    clear(): void {
        this.$mask.removeClass('show');
        while (this.$mask.hasClass('show')) {
            this.$mask.removeClass('show');
        }
        setTimeout(() => {
            this.$mask.removeClass('show');
        }, 10);
        Toolkit.html(this.$dragTransfer, '');
        this.$dragTransfer.css({
            width: 0,
            height: 0,
            transform: `translate(0, 0)`
        });
        this.disX = 0;
        this.disY = 0;
        this.dragTargets && this.startAnimation(this.dragTargets);
    }

    // 筛选商品，并按横轴排序
    findDragLayerCubes(): CubeId[] {
        let layerCubes: Set<CubeId> = new Set();
        let sortTemp: Array<FreeObjMap> = [];
        let result: CubeId[];

        this.dragTargets.forEach((cubeId: CubeId): void => {
            if (CubeTree.isGoods(cubeId)
                || CubeTree.isBafflePlate(cubeId)
                || CubeTree.isTextarea(cubeId)) {
                layerCubes.add(cubeId);
            }
        });
        layerCubes.forEach((cubeId: CubeId): void => {
            sortTemp.push({
                id: cubeId,
                left: CodiSystem.getLogicCoordinate(cubeId).left
            });
        });
        result = Utils.quickSortByKey(sortTemp, 'left').map((temp: FreeObjMap): CubeId => {
            return temp.id;
        });

        return result;
    }

    findDragCabinetCubes(): CubeId[] {
        let result: Set<CubeId> = new Set();

        this.dragTargets.forEach((cubeId: CubeId): void => {
            if (CubeTree.isGoods(cubeId)
                || CubeTree.isTextarea(cubeId)) {
                result.add(cubeId);
            }
        });

        return Array.from(result);
    }

    // 筛选货架
    findDragShelf(): CubeId[] {
        let shelfs: Set<CubeId> = new Set();
        let sortTemp: Array<FreeObjMap> = [];
        let result: CubeId[];

        this.dragTargets.forEach((cubeId: CubeId): void => {
            if (CubeTree.isShelf(cubeId) || CubeTree.isFreezer(cubeId)) {
                shelfs.add(cubeId);
            }
        });
        shelfs.forEach((cubeId: CubeId): void => {
            sortTemp.push({
                id: cubeId,
                left: CodiSystem.getLogicCoordinate(cubeId).left
            });
        });
        result = Utils.quickSortByKey(sortTemp, 'left').map((temp: FreeObjMap): CubeId => {
            return temp.id;
        });

        return Array.from(result);
    }

    // 筛选层板，todo: 排序
    findDragLayers(): CubeId[] {
        let layers: Set<CubeId> = new Set();

        this.dragTargets.forEach((cubeId: CubeId): void => {
            if (CubeTree.isPartition(cubeId)) {
                layers.add(cubeId);
            }
        });

        return Array.from(layers);
    }

    findPressCube(): CubeId {
        return this.pressCube;
    }

    private stopAnimation(cubes: CubeId[]): void {
        cubes.forEach((cubeId: CubeId): void => {
            let cube: CubeNode|null = CubeTree.findByCubeId(cubeId);

            if (CubeTree.isShelf(cubeId)) {
                let nextShelf: CubeId|null = CubeTree.findNextShelf(cubeId);
                let shelf: CubeNode|null = CubeTree.findByCubeId(nextShelf);

                shelf && shelf.jView.addClass('stop-border-animation');
            }

            cube && cube.jView.addClass('stop-border-animation');
        });
    }

    private startAnimation(cubes: CubeId[]): void {
        cubes.forEach((cubeId: CubeId): void => {
            let cube: CubeNode|null = CubeTree.findByCubeId(cubeId);

            if (CubeTree.isShelf(cubeId)) {
                let nextShelf: CubeId|null = CubeTree.findNextShelf(cubeId);
                let shelf: CubeNode|null = CubeTree.findByCubeId(nextShelf);

                shelf && shelf.jView.removeClass('stop-border-animation');
            }

            cube && cube.jView.removeClass('stop-border-animation');
        });
    }

    // 计算子rect相对于父rect的相对坐标
    private createChildRect(rects: CubePaintCoordinate[], box: Rect): CubePaintCoordinate[] {
        let result: CubePaintCoordinate[] = [];

        rects.forEach((rect: CubePaintCoordinate): void => {
            let temp: CubePaintCoordinate = {
                id: rect.id,
                width: rect.width,
                height: rect.height,
                left: Utils.math(rect.left, box.left, '-'),
                top: Utils.math(rect.top, box.top, '-')
            };

            result.push(temp);
            this.childRelativeRectMap[rect.id] = temp;
        });

        return result;
    }

    private createChildItem(rects: CubePaintCoordinate[]): JQuery<HTMLElement> {
        let box: HTMLElement = document.createElement('div');

        rects.forEach((rect: CubePaintCoordinate): void => {
            let dragItem: HTMLElement = this.createDragItem(rect);

            box.appendChild(dragItem);
        });

        return $(box).children();
    }

    private createDragItem(rect: CubePaintCoordinate): HTMLElement {
        let $dragItem: JQuery<HTMLElement> = this.createDragBox(rect);
        let $cubeUI: JQuery<HTMLElement> = this.cloneCubeView(rect.id);

        $dragItem.append($cubeUI);

        return $dragItem.get(0);
    }

    private cloneCubeView(cubeId: CubeId): JQuery<HTMLElement> {
        let $result: JQuery<HTMLElement> = $(document.createElement('div'));
        let cube: CubeNode = CubeTree.findByCubeId(cubeId);

        // 放置拖动瞬间卡顿，延迟cloneUI
        let $clone: JQuery<HTMLElement> = $(cube.jView.get(0).cloneNode(true));
        let $mask: JQuery<HTMLElement> = this.createMask();
        let scale: number = Config.get({
            moduleName: 'transform',
            key: 'scale'
        });

        if (CubeTree.isBeamsCube(cubeId)) {
            $clone.addClass('is-beamCube');
        }

        if (CubeTree.isGoods(cubeId)
            || CubeTree.isTextarea(cubeId)
            || CubeTree.isBafflePlate(cubeId)) {
            $clone.find('.goods-table-td').css('box-shadow', 'none');
        }

        // 移除无用dom节点
        $clone.find(`
            .flow-border-left,
            .flow-border-right,
            .flow-border-top,
            .flow-border-bottom,
            .shelvesbuild-e-g-default__in-layer,
            .is-in-stage
        `).remove();
        $clone.removeClass('cube--selected')
            .removeClass('drag-hide')
            .css({
                position: 'absolute',
                left: '50%',
                top: '50%',
                bottom: '',
                transform: `translate(-50%, -50%) scale(${scale})`
            })
            .append($mask);
        $result.append($clone);
        $result.closest('.shelvesbuild__drag-item').removeClass('shelvesbuild__drag-item--rect');

        return $result;
    }

    private createDragBox(rect: FreeObjMap): JQuery<HTMLElement> {
        let box: HTMLElement = document.createElement('div');
        box.className = 'shelvesbuild__drag-item shelvesbuild__drag-item--rect';
        let $box: JQuery<HTMLElement> = $(box);

        $box.css({
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            left: `${rect.left}px`,
            top: `${rect.top}px`
        });

        return $box;
    }

    private createMask(): JQuery<HTMLElement> {
        let result: HTMLElement = document.createElement('div');

        result.style.position = 'absolute';
        result.style.left = '0px';
        result.style.top = '0px';
        result.style.width = '100%';
        result.style.height = '100%';
        result.style.zIndex = '10';

        return $(result);
    }
}

export default new DragTransfer();
